import React from "react";
import LeftVector from "../../assets/faq/left-Patterns.svg";
import RightVector from "../../assets/faq/right-element.svg";
import Adaptability from "../../assets/hiring_quide/employee-adaptability.webp";
import Punctuality from "../../assets/hiring_quide/employee-punctuality.webp";
import Consistency from "../../assets/hiring_quide/employee-consistency.webp";

const WhyMsmesStruggleToHireBlueCollar = () => {
    const blogPosts = [
        {
            title: "Days 1–2: Punctuality & Interest",
            bullets: [
                "Are they arriving on time?",
                "Do they ask questions and show curiosity during onboarding?",
                "Do they look motivated to learn?",
            ],
            note: "Early enthusiasm is often the strongest signal of long-term commitment.",
            link: "#",
            image: Punctuality,
        },
        {
            title: "Days 3–5: Adaptability",
            bullets: [
                "How quickly are they learning the basics of their role?",
                "Are they cooperating with team members?",
                "Do they handle small challenges without hesitation?",
            ],
            note: "If they can adjust to the work pace and culture in this phase, they’re less likely to drop out suddenly.",
            link: "#",
            image: Adaptability,
        },
        {
            title: "Days 6–7: Consistency",
            bullets: [
                "Are they still showing the same energy as Day 1?",
                "Are they building a rhythm of attendance and task completion?",
            ],
            note: "Consistency in the first week is a predictor of reliability for the next 3–6 months.",
            link: "#",
            image: Consistency,
        },
    ];

    return (
        <section className="relative bg-white overflow-hidden">
            <img
                src={LeftVector}
                alt="Left background"
                className="absolute top-0 left-0 w-80 md:w-49 lg:w-[300px] pointer-events-none opacity-50"
            />
            <img
                src={RightVector}
                alt="Right background"
                className="absolute bottom-0 right-0 w-80 md:w-96 lg:w-[300px] pointer-events-none opacity-50"
            />

            <div className="relative z-10 px-6 py-12 max-w-6xl mx-auto">
                {/* Header */}
                <div className="px-6 md:px-20 lg:px-24 pt-20 pb-6">
                    {/* Title (centered) */}
                    <h2
                        className="text-3xl md:text-5xl font-bold mb-4 text-center"
                        style={{ color: "#fa6011" }}
                    >
                        Forget 3 Months — Employee Retention Is Decided in Just 7 Days
                    </h2>
                </div>
                <div className="max-w-6xl mx-auto">
                    <p className="text-left text-lg md:text-xl text-gray-700 leading-relaxed font-semibold mb-4">
                        When it comes to blue-collar and entry-level hiring,
                        most employers assume that retention issues show up after 2–3 months.
                        But data and real-world experience tell a different story:
                        The majority of dropouts actually happen within the first week of joining.
                    </p>
                </div>
                <div className="flex flex-wrap justify-center gap-8 mb-16">
                    {blogPosts.map((post, index) => (
                        <div
                            key={index}
                            className="w-full sm:w-[48%] md:w-[45%] lg:w-[40%] h-[550px] max-w-[500px] rounded-[30px] bg-white shadow-lg p-8 flex flex-col items-start text-left"
                        >
                            <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-56 object-cover rounded-lg border mb-6"
                            />
                            <h3 className="text-xl font-semibold mb-3">{post.title}</h3>

                            {/* If bullets exist, render as list */}
                            {post.bullets ? (
                                <>
                                    <ul className="list-disc pl-5 text-base text-gray-600 mb-3">
                                        {post.bullets.map((point, i) => (
                                            <li key={i}>{point}</li>
                                        ))}
                                    </ul>
                                    {post.note && (
                                        <p className="text-base text-gray-600">{post.note}</p>
                                    )}
                                </>
                            ) : (
                                <p className="text-base text-gray-600">{post.subtitle}</p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="max-w-3xl mx-auto p-4">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center"
                        style={{ color: "#fa6011" }}>
                        How Kaigal Makes Hiring Easy
                    </h2>
                </div>
                <div className="m-8 sm:m-16 pb-20">

                    {/* Row 1 with thicker vertical divider */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-10 divide-y lg:divide-y-0 lg:divide-x-2 divide-gray-300 pb-8">

                        {/* Grid 1 */}
                        <div className="p-6 text-left">
                            <span className="text-2xl font-semibold mb-4 block">Industry insights confirm this:</span>
                            <ul className="list-disc pl-5 text-base text-gray-600 space-y-2">
                                <li>
                                    Workers who cross the first 7 days without absenteeism or disengagement
                                    are significantly more likely to stay for a longer period — sometimes
                                    double the retention rate compared to those who stumble early.
                                </li>
                            </ul>
                        </div>

                        {/* Grid 3 */}
                        <div className="p-6 text-left">
                            <span className="text-2xl font-semibold mb-4 block">Pro Tip from Kaigal</span>
                            <ul className="list-disc pl-5 text-base text-gray-600 space-y-2">
                                <li>Provide clear job instructions in their regional language.</li>
                                <li>Give small wins early — it boosts confidence and loyalty.</li>
                            </ul>
                        </div>
                    </div>

                    {/* Horizontal divider (75% width) */}
                    <div className="flex justify-center my-8">
                        <div className="w-3/4 h-[2px] bg-gray-300 rounded-full"></div>
                    </div>

                    {/* Row 2 */}
                    <div className="p-6 text-left">
                        <span className="text-2xl font-semibold mb-4 block">The Takeaway for Employers</span>
                        <ul className="list-disc pl-5 text-base text-gray-600 space-y-2">
                            <li>
                                Instead of waiting until the 90-day probation period to evaluate employees,
                                MSMEs and HR professionals should pay very close attention to the first 7 days.
                            </li>
                            <li>That’s when habits are formed, interest is tested, and retention begins.</li>
                            <li>
                                By making the first week structured, engaging, and supportive,
                                you can cut down early attrition and build a more stable workforce.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyMsmesStruggleToHireBlueCollar;